import React, { useState } from 'react';
import {
    Avatar,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    Grid,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    Paper,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    LockOutlined as LockOutlinedIcon,
    Visibility,
    VisibilityOff,
    EmailOutlined,
    PersonOutlined,
    PhoneOutlined,
    ArrowForward,
    InfoOutlined
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { primarycolor } from '../Utils/colors';
import ReactLoading from 'react-loading';
import { setsessionuser } from '../Utils/sessions';
import fetch from '../Utils/fetch';
import { Successalert } from '../Components/popups';

const loadingType = "spokes";
const loadingColor = "white";


export default function SignUp({ successsignup, cancelsignup }) {
    const [showPassword, setShowPassword] = useState(false);
    const [progress, setProgress] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError(null);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const { firstName, lastName, email, phone, password } = formData;

        if (!email || !password || !firstName || !lastName || !phone) {
            setError("Please fill in all fields to continue.");
            return;
        }

        setError(null);
        setOpenConfirm(true); // Open confirmation first
    };

    const handleConfirmRegistration = () => {
        setOpenConfirm(false);
        setProgress(true);
        const { firstName, lastName, email, phone, password } = formData;

        fetch.get('requestCustomerAccount', {
            customerName: `${firstName} ${lastName}`,
            customerEmail: email,
            customerPhone: phone,
            password: password
        }).then(response => {
            setProgress(false);
            if (!response.error) {
                let data = response.list ? response.list[0] : response;
                if (data) {
                    setsessionuser(data);
                    setSuccess("Registration successful! Redirecting...");
                    // Wait for the success alert to show before dismissing
                    setTimeout(() => {
                        successsignup();
                    }, 3000);
                } else {
                    setError("An account could not be created. Please contact support.");
                }
            } else {
                setError(response.message || "There was an error creating your account.");
            }
        }).catch(err => {
            setProgress(false);
            setError(err || "A connection error occurred. Please try again later.");
        });
    };

    return (
        <Fade in={true} timeout={800}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: 450,
                    mx: 'auto',
                }}
            >
                <Avatar sx={{ m: 1, bgcolor: primarycolor, width: 44, height: 44, mb: 1 }}>
                    <LockOutlinedIcon fontSize="medium" />
                </Avatar>

                <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Join Us
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    Create an account to start shopping.
                </Typography>

                <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%' }}>
                    <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                            <TextField
                                name="firstName"
                                required
                                fullWidth
                                label="First Name"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonOutlined color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                required
                                fullWidth
                                label="Last Name"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonOutlined color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                required
                                fullWidth
                                label="Email Address"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailOutlined color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                required
                                fullWidth
                                label="Phone Number"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PhoneOutlined color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={handleInputChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockOutlinedIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox value="remember" color="primary" />}
                                label={<Typography variant="body2">I agree to the Terms and Conditions</Typography>}
                            />
                        </Grid>
                    </Grid>

                    {error && (
                        <Fade in={!!error}>
                            <Typography
                                color="error"
                                sx={{
                                    mt: 2,
                                    fontSize: '0.85rem',
                                    textAlign: 'center',
                                    bgcolor: 'error.main' + '15',
                                    p: 1,
                                    borderRadius: 1
                                }}
                            >
                                {error}
                            </Typography>
                        </Fade>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="medium"
                        disabled={progress}
                        sx={{
                            mt: 2.5,
                            mb: 2,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1rem',
                            boxShadow: `0 4px 12px ${primarycolor}40`,
                            '&:hover': {
                                boxShadow: `0 6px 16px ${primarycolor}60`,
                            }
                        }}
                    >
                        {progress ? (
                            <ReactLoading type={loadingType} color={loadingColor} height={24} width={24} />
                        ) : (
                            <>
                                Sign Up <ArrowForward sx={{ ml: 1, fontSize: 20 }} />
                            </>
                        )}
                    </Button>

                    <Grid container justifyContent="center">
                        <Grid item>
                            <Link
                                to="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    cancelsignup();
                                }}
                                style={{
                                    color: primarycolor,
                                    fontSize: '0.95rem',
                                    textDecoration: 'none',
                                    fontWeight: 500
                                }}
                            >
                                Already have an account? Sign in
                            </Link>
                        </Grid>
                    </Grid>
                </Box>

                {/* Success Notification */}
                {success && <Successalert message={success} onClose={() => setSuccess(null)} />}

                {/* Confirmation Dialog */}
                <Dialog
                    open={openConfirm}
                    onClose={() => setOpenConfirm(false)}
                    PaperProps={{
                        sx: { borderRadius: 3, p: 1 }
                    }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoOutlined color="primary" /> Confirm Details
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Please review your information before we create your account:
                        </DialogContentText>
                        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2 }}>
                            <Typography variant="body2"><strong>Name:</strong> {formData.firstName} {formData.lastName}</Typography>
                            <Typography variant="body2"><strong>Email:</strong> {formData.email}</Typography>
                            <Typography variant="body2"><strong>Phone:</strong> {formData.phone}</Typography>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2, pt: 0 }}>
                        <Button onClick={() => setOpenConfirm(false)} color="inherit" sx={{ textTransform: 'none' }}>
                            Edit Details
                        </Button>
                        <Button
                            onClick={handleConfirmRegistration}
                            variant="contained"
                            autoFocus
                            sx={{ textTransform: 'none', px: 3, borderRadius: 2 }}
                        >
                            Confirm & Sign Up
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Fade>
    );
}
